import type { PostHog } from 'posthog-js';

// Usage, errors and performance go to PostHog (EU cloud). Until a visitor agrees, and if they
// decline, without cookies or any storage that follows them around. Those who agree (the consent
// banner, src/components/ui/consent-banner.tsx) get cookies, so that their visits link up, and
// their sessions recorded, which needs them. Only the production build reports, and only when a
// project key is set (VITE_POSTHOG_KEY in .env.production), so neither local development nor the
// slideshow recordings add to the numbers.
const KEY = import.meta.env.VITE_POSTHOG_KEY;
const ENABLED = import.meta.env.PROD && Boolean(KEY);

// The visitor's answer to the consent banner, or 'unknown' until the library has loaded and read it
export type TConsent = 'unknown' | 'pending' | 'granted' | 'denied';

// Outside production there is no library to ask: the banner shows, to be seen while developing,
// and the answer is kept for the page only
let consent: TConsent = ENABLED ? 'unknown' : 'pending';
const consentListeners = new Set<() => void>();
const setConsentState = (next: TConsent) => {
	consent = next;
	consentListeners.forEach((listener) => listener());
};

export const getConsent = (): TConsent => consent;

export const subscribeConsent = (listener: () => void) => {
	consentListeners.add(listener);
	return () => consentListeners.delete(listener);
};

/** The visitor's answer, kept by the library in its own cookie, so the banner does not come back. */
export const answerConsent = (granted: boolean) => {
	if (granted) client?.opt_in_capturing();
	else client?.opt_out_capturing();
	setConsentState(granted ? 'granted' : 'denied');
	// Opting in sends the library's own $opt_in
	if (!granted) track('consent_declined');
};

let client: PostHog | null = null;
// Events from before the library has arrived, sent once it has
const pending: [string, Record<string, unknown> | undefined][] = [];

// Errors from before the library has arrived, which its own handlers would not see
const pendingErrors: [unknown, Record<string, unknown> | undefined][] = [];

export const track = (event: string, properties?: Record<string, unknown>) => {
	if (!ENABLED) return;
	if (client) client.capture(event, properties);
	else pending.push([event, properties]);
};

/** Reports an error that was caught, and so would not reach PostHog on its own. */
export const reportError = (error: unknown, properties?: Record<string, unknown>) => {
	if (!ENABLED) return;
	if (client) client.captureException(error, properties);
	else pendingErrors.push([error, properties]);
};

const onEarlyError = (event: ErrorEvent) => reportError(event.error ?? event.message);
const onEarlyRejection = (event: PromiseRejectionEvent) => reportError(event.reason);

// A phone closes a tab that runs out of graphics memory without any error reaching the page. A
// flag held while the app runs, cleared when the page is left normally, tells the next load in
// the same tab that the previous one died; sessionStorage survives the reload a phone does then.
const RUNNING_KEY = 'all2scale-running';

const readFlag = () => {
	try {
		return sessionStorage.getItem(RUNNING_KEY);
	} catch {
		return null;
	}
};
const writeFlag = (value: string | null) => {
	try {
		if (value === null) sessionStorage.removeItem(RUNNING_KEY);
		else sessionStorage.setItem(RUNNING_KEY, value);
	} catch {
		// Storage can be unavailable, in a private window for instance: the check is skipped
	}
};

let runningDataset = '';

/** Records which dataset is shown, so that a crash can be put down to it. */
export const noteDataset = (id: string) => {
	runningDataset = id;
	writeFlag(id);
};

export const initAnalytics = () => {
	if (!ENABLED || !KEY) return;

	const crashedDataset = readFlag();
	if (crashedDataset !== null) track('previous_load_crashed', { dataset: crashedDataset || null });
	writeFlag(runningDataset);
	window.addEventListener('pagehide', () => writeFlag(null));
	// Coming back from the browser's page cache is the page running again
	window.addEventListener('pageshow', (event) => event.persisted && writeFlag(runningDataset));
	// Until the library has loaded and set up its own handlers
	window.addEventListener('error', onEarlyError);
	window.addEventListener('unhandledrejection', onEarlyRejection);

	import('posthog-js').then(({ default: posthog }) => {
		posthog.init(KEY, {
			// Through the site's own address (see worker/index.ts), which tracker blockers let through
			api_host: `${window.location.origin}/ingest`,
			ui_host: 'https://eu.posthog.com',
			// No cookies or local storage until the visitor agrees, nor if they decline: visitors are
			// then told apart by a daily hash on PostHog's side, which needs cookieless mode switched
			// on in the project's settings. Undecided counts as declined, so that they are counted
			// as before the banner existed. Sessions are recorded only once they agree: the
			// library cannot record without storage.
			cookieless_mode: 'on_reject',
			opt_out_capturing_by_default: true,
			// The first load only: the app rewrites the URL as the camera moves
			capture_pageview: true,
			// Leaving the page closes the visit, which is how its length and bounces are measured
			capture_pageleave: true,
			capture_exceptions: true,
			capture_performance: { web_vitals: true, network_timing: false },
			// Only the events the app sends itself
			autocapture: false,
			rageclick: false,
			capture_dead_clicks: false,
			capture_heatmaps: false,
			disable_surveys: true,
		});
		client = posthog;
		setConsentState(posthog.get_explicit_consent_status());
		window.removeEventListener('error', onEarlyError);
		window.removeEventListener('unhandledrejection', onEarlyRejection);
		pending.splice(0).forEach(([event, properties]) => posthog.capture(event, properties));
		pendingErrors.splice(0).forEach(([error, properties]) => posthog.captureException(error, properties));
	});
};
